import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';
import { geoMenuItems as menuItems } from '../components/Sidebar';

const ALL_PERMISSIONS = [
  { group: 'SEO 矩阵洞察系统', perms: [
    { key: 'menu:seo', label: '允许访问 SEO 系统' },
    { key: 'page:seo_overview', label: '监控总览' },
    { key: 'page:seo_dashboard', label: '引擎表现大盘' },
    { key: 'page:seo_brand_insights', label: '品牌数据洞察' },
    { key: 'page:seo_media', label: '媒体/达人占位' },
    { key: 'page:seo_compare', label: '对比分析报告' },
    { key: 'page:seo_tasks', label: '定时抓取任务 & 订阅大盘' },
    { key: 'page:seo_results', label: '抓取结果明细' },
    { key: 'page:seo_config', label: '战略资产库配置' },
    { key: 'page:seo_engines', label: '平台与抓取配置' }
  ]},
  { group: 'GEO 全景监控系统', perms: [
    { key: 'menu:geo', label: '允许访问 GEO 系统' },
    { key: 'page:dashboard', label: '监控总览' },
    { key: 'page:engine_monitor', label: '引擎表现大盘' },
    { key: 'page:brand_insights', label: '品牌数据洞察' },
    { key: 'page:mention_records', label: '溯源明细记录' },
    { key: 'page:reports', label: '对比分析报告' },
    { key: 'page:tasks', label: '定时抓取任务 & 订阅大盘' },
    { key: 'page:results', label: '抓取结果明细' },
    { key: 'page:geo_brands', label: '品牌与竞品配置' },
    { key: 'page:geo_prompts', label: '抓取词库管理' },
    { key: 'page:geo_engines', label: '引擎与抓取配置' },
    { key: 'action:manual_upload', label: '允许人工截图录入 (通义MB等)' }
  ]},
  { group: '电商转化归因模块', perms: [
    { key: 'menu:commerce', label: '允许访问电商转化归因菜单' },
    { key: 'page:commerce_matrix', label: '商品卡位矩阵' },
    { key: 'page:commerce_analytics', label: '流量归因分析' },
    { key: 'page:commerce_data_hub', label: '罗盘数据接入' }
  ]},

  { group: '语料资产库', perms: [
    { key: 'menu:corpus', label: '允许访问语料资产库菜单' },
    { key: 'corpus:view', label: '允许查看语料资产详情' },
    { key: 'corpus:edit', label: '允许编辑和采纳语料资产' },
    { key: 'corpus:audit', label: '允许审核语料资产 (入库/驳回)' },
    { key: 'page:corpus_collection', label: '品牌官方语料' },
    { key: 'page:corpus_crawl', label: '生态溯源回流' },
    { key: 'page:corpus_repository', label: '结构化语料池' },
    { key: 'page:corpus_insights', label: '智能推荐洞察' }
  ]},
  { group: 'SEM 竞价管理系统 (开发中)', perms: [
    { key: 'menu:sem', label: '允许访问 SEM 系统' }
  ]},
  { group: 'Feeds 信息流系统 (开发中)', perms: [
    { key: 'menu:feeds', label: '允许访问 Feeds 系统' }
  ]},
  { group: '系统管理与后台', perms: [
    { key: 'menu:system', label: '允许访问系统设置菜单' },
    { key: 'page:users', label: '用户管理' },
    { key: 'page:user_groups', label: '用户组管理' },
    { key: 'page:user_manage', label: '角色与权限配置' },
    { key: 'page:cost', label: '系统扣费配置' },
    { key: 'page:api_config', label: 'API 接口配置' },
    { key: 'page:announcements', label: '通知公告发布' },
    { key: 'page:logs', label: '系统日志查看' },
    { key: 'page:all', label: '最高超级管理员权限 (含所有系统)' }
  ]}
];

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    permissions: []
  });

  // Flatten menu items for easy label lookup in table
  const allPermsMap = { 'page:all': '所有权限 (超级管理员)' };
  menuItems.forEach(item => {
    if (item.permission) allPermsMap[item.permission] = item.label;
    if (item.children) {
      item.children.forEach(child => {
        if (child.permission) allPermsMap[child.permission] = child.label;
      });
    }
  });

  


  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/roles`);
      setRoles(res.data);
    } catch (error) {
      console.error('Failed to fetch roles', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      let parsedPerms = [];
      try {
        parsedPerms = JSON.parse(role.permissions);
      } catch (err) { /* ignore */ }
      setFormData({
        name: role.name,
        displayName: role.displayName,
        permissions: parsedPerms
      });
    } else {
      setEditingRole(null);
      setFormData({ name: '', displayName: '', permissions: [] });
    }
    setIsModalOpen(true);
  };

  const handleTogglePermission = (key) => {
    setFormData(prev => {
      const current = prev.permissions || [];
      if (current.includes(key)) {
        return { ...prev, permissions: current.filter(p => p !== key) };
      } else {
        return { ...prev, permissions: [...current, key] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API_URL}/admin/roles/${editingRole.id}`, formData);
      } else {
        await axios.post(`${API_URL}/admin/roles`, formData);
      }
      setIsModalOpen(false);
      fetchRoles();
    } catch (error) {
      alert(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('确定要删除此角色吗？如果有用户绑定了该角色可能会受影响。')) {
      try {
        await axios.delete(`${API_URL}/admin/roles/${id}`);
        fetchRoles();
      } catch (error) {
        alert(error.response?.data?.error || '删除失败');
      }
    }
  };

  if (loading) return <div className="p-8 text-gray-500 dark:text-gray-400">加载中...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">角色与权限管理</h1>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
        >
          <Plus size={20} className="mr-2" />
          新增角色
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800/50 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-100 dark:border-slate-700">
            <tr>
              <th className="text-left py-4 px-6 font-medium text-gray-500 dark:text-gray-400">角色标识</th>
              <th className="text-left py-4 px-6 font-medium text-gray-500 dark:text-gray-400">角色名称</th>
              <th className="text-left py-4 px-6 font-medium text-gray-500 dark:text-gray-400">包含权限</th>
              <th className="text-right py-4 px-6 font-medium text-gray-500 dark:text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {roles.map(role => {
              let permsList = [];
              if (Array.isArray(role.permissions)) {
                permsList = role.permissions;
              } else if (typeof role.permissions === 'string') {
                try { permsList = JSON.parse(role.permissions); } catch(err) { /* ignore */ }
              }
              const isSuper = permsList.includes('page:all');
              const pCount = permsList.length;
              return (
                <tr key={role.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                  <td className="py-4 px-6 font-mono text-sm text-gray-600 dark:text-gray-400">{role.name}</td>
                  <td className="py-4 px-6 font-medium dark:text-gray-200">{role.displayName}</td>
                  <td className="py-4 px-6">
                    <div className="flex flex-wrap gap-1">
                      {isSuper ? (
                        <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs rounded font-medium border border-red-200 dark:border-red-800/50">超级管理全权限</span>
                      ) : (
                        <>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 text-xs rounded border border-indigo-200 dark:border-indigo-800/50">
                            已授权 {pCount} 项功能
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => handleOpenModal(role)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="编辑"
                    >
                      <Edit2 size={18} />
                    </button>
                    {role.name !== 'super_admin' && (
                      <button
                        onClick={() => handleDelete(role.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-2"
                        title="删除"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200 dark:border-slate-700 rounded-2xl w-full max-w-2xl p-6 shadow-2xl">
            <h2 className="text-xl font-bold mb-6 dark:text-white">{editingRole ? '编辑角色' : '新增角色'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">角色标识 (英文字母)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg"
                    disabled={!!editingRole}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称</label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={e => setFormData({...formData, displayName: e.target.value})}
                    className="w-full p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">权限分配</label>
                <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg border border-gray-200 dark:border-slate-700 max-h-96 overflow-y-auto custom-scrollbar">
                  {/* Super Admin Checkbox */}
                  <label className="flex items-center space-x-2 cursor-pointer mb-4 pb-4 border-b border-gray-200">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes('page:all')}
                      onChange={() => handleTogglePermission('page:all')}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                    />
                    <span className="text-sm font-bold text-red-600">所有权限 (超级管理员 - 勾选后无视下方设定)</span>
                  </label>

                  <div className="space-y-6">
                {ALL_PERMISSIONS.map((group, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg border border-gray-100 dark:border-slate-700/50">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-200 mb-3 pb-2 border-b border-gray-200 dark:border-slate-700">{group.group}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {group.perms.map(p => (
                        <label key={p.key} className="flex items-center space-x-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={(formData.permissions || []).includes(p.key)}
                            onChange={() => handleTogglePermission(p.key)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-slate-700 dark:border-slate-600"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            {p.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
